import ImpressionLoggerWithCallback from '@/components/common/ImpressionLoggerWithCallback';
import { AdControlNativeModel } from '@/entities/advertisement';
import useNavigate from '@/hooks/useNavigate';
import { isWebView } from '@/utils/mobile/RNPlugins';
import { cn } from '@/utils/style';
import React, { useCallback, useEffect } from 'react';

interface AdropQnaNativeProps {
  adData: AdControlNativeModel;
  className?: string;
  fallback: React.ReactNode;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  isShowAdBadge?: boolean;
}

const AdropQnaNative: React.FC<AdropQnaNativeProps> = (props) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (props.adData?.isVisible) {
      props.onLoad?.();
    }
  }, [props.adData?.isVisible]);

  const handleImpressionLog = useCallback(async () => {
    if (!props.adData) {
      return;
    }
    await props.adData.trackImpression();
  }, [props.adData]);

  const handleQnaClick = async () => {
    if (isWebView()) {
      await props.adData.trackClick();
      navigate.navigateToExternalBrowser({ url: props.adData.getDestinationUrl() });
      return;
    }
    props.adData?.handleNativeClick();
  };

  const nativeData = props.adData.nativeData;

  if (!nativeData) {
    return (
      <div className={'w-full'}>
        <div className='min-h-0'>{props.fallback}</div>
      </div>
    );
  }

  return (
    <>
      <div className={'w-full border-gray-200 hover:bg-gray-30'}>
        <div className='min-h-0'>
          <ImpressionLoggerWithCallback onImpressionLog={handleImpressionLog}>
            <div className={cn('relative', props.className)}>
              <div className='box-border flex cursor-pointer flex-col gap-[24px] p-[24px]' onClick={handleQnaClick}>
                <div className='absolute inset-x-0 bottom-0 border-gray-200 border-b' />
                <div className='flex flex-col gap-[12px]'>
                  <div className='flex flex-col gap-[12px]'>
                    <div className='flex flex-col gap-[12px]'>
                      <div className='flex w-full items-center justify-between'>
                        <div className='flex flex-1 flex-col gap-[4px] pr-[16px]'>
                          <div className='typography-ui-17sb overflow-hidden overflow-ellipsis whitespace-nowrap text-gray-800'>
                            {nativeData.headline}
                          </div>
                          <div
                            className='typography-ui-13r -webkit-box flex flex-col justify-center overflow-hidden overflow-ellipsis text-gray-500'
                            style={{ WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                          >
                            {nativeData.body}
                          </div>
                        </div>
                      </div>
                      {(nativeData.tag || nativeData.extra_tag) && (
                        <div className='flex items-center gap-[4px]'>
                          {nativeData.tag && (
                            <div className='flex h-[24px] items-center justify-center rounded-[4px] bg-gray-20 px-[8px] py-0'>
                              <div className='typography-ui-13sb text-gray-500'>{nativeData.tag}</div>
                            </div>
                          )}
                          {nativeData.extra_tag && (
                            <div className='flex h-[24px] items-center justify-center rounded-[4px] bg-gray-20 px-[8px] py-0'>
                              <div className='typography-ui-13sb text-gray-500'>{nativeData.extra_tag}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {props.isShowAdBadge && (
                <div className='absolute top-[12px] right-[12px] flex h-[18px] w-[28px] items-center justify-center rounded-[5px] bg-dimmed-20'>
                  <p className='typography-ui-12m text-white'>AD</p>
                </div>
              )}
            </div>
          </ImpressionLoggerWithCallback>
        </div>
      </div>
      <div className={'h-[1px] w-full bg-gray-40'} />
    </>
  );
};

export default AdropQnaNative;
